import { IOgqlV1Column, IOgqlColumn, BasicColumnTypes } from '@opengov/ogql-types';
import { IAccountSummaryFormatting } from './accountSummary';
import { IReportConfigurationPresentation, IReportConfiguration } from './reportConfiguration';

export enum EmbedTypeV3 {
  GroupedBarChart = 'grouped_bar_chart',
  BarChart = 'bar_chart',
  StackedBarChart = 'stacked_bar_chart',
  HorizontalBarChart = 'horizontal_bar_chart',
  StackedHorizontalBarChart = 'stacked_horizontal_bar_chart',
  LineChart = 'line_chart',
  PieChart = 'pie_chart',
  Gauge = 'gauge'
}

export enum EmbedTypeV4 {
  GroupedBarChart = 'grouped_bar_chart',
  StackedBarChart = 'stacked_bar_chart',
  HorizontalGroupedBarChart = 'horizontal_grouped_bar_chart',
  StackedHorizontalBarChart = 'stacked_horizontal_bar_chart',
  LineChart = 'line_chart',
  PieChart = 'pie_chart',
  Gauge = 'gauge',
  Donut = 'donut'
}

interface ITitle {
  text: string;
}

interface IXAxis {
  title: ITitle;
  categories: string[];
}

interface ISeriesData {
  y: number;
  percentTotal: number;
  total: number;
  targetStatus: number;
  percentChange?: number | string;
}
interface ISeries {
  name: string;
  data: ISeriesData[];
  color?: string;
}

interface IPlotLineLabel {
  align: string;
  text: string;
  useHTML: boolean;
  verticalAlign: string;
  y: number;
}

interface IPlotLine {
  color: string;
  dashStyle: string;
  width: number;
  zIndex: number;
  label: IPlotLineLabel;
  value: string;
}

interface IYAxis {
  title: ITitle;
  allowDecimals: boolean;
  plotLines: IPlotLine[];
  softMax?: number | null;
}

interface IPieData {
  name: string;
  y: number;
  percentTotal?: number;
  color?: string;
}

interface IPieSeries {
  name: string;
  data: IPieData[];
}

export interface IMdsColRowFieldsOptions {
  columnChoices: string[][];
  rowChoices: string[][];
  columnId: string;
  rowGroupId: string;
  targetColumnId: string;
}

export interface IMdsGaugeOptions {
  measurementColumnId: string;
  targetColumnId: string;
  rowGroupId: string;
}

export interface IMdsPieOptions {
  columnId: string;
  columnChoices: string[][];
}

export type GetChartData<ResponseType = IHighchartsResponse> = (args: {
  options: IChartDataOptions;
  data?: any[][];
  columns?: IOgqlV1Column[] | IOgqlColumn[];
  type?: EmbedTypeV3;
}) => ResponseType;

export interface IChartDataOptions {
  targetConditions?: {
    operator: string;
    status: {
      text: string;
    };
    value: {
      valueType: string;
      text: string;
    };
  }[];
  entityFiscalYearStartMonth: number;
  organizedByColumnDisplayName: string;
  organizedByColumnType: BasicColumnTypes;
  organizedByFormattingOption: string;
  showColumnAsPercentOfTotal: boolean;
  showColumnDisplayName: string;
  showColumnDisplayType: string;
  colSortOrder?: string;
  colorMap?: IReportConfigurationPresentation['colors'];
  hiddenLegendItemsSet: Set<string>;
  hideTargetConditions: boolean;
  labelsEnabled?: boolean;
  axisTitlesEnabled?: boolean;
}

export interface IMdsChartDataOptions {
  targetConditions?: IChartDataOptions['targetConditions'];
  pieOptions?: IMdsPieOptions;
  colorMap?: IReportConfigurationPresentation['colors'];
}

interface IHighchartsBaseResponse {
  showColumnDisplayType?: IChartDataOptions['showColumnDisplayType'];
  showColumnAsPercentOfTotal?: IChartDataOptions['showColumnAsPercentOfTotal'];
}

export interface IHighchartsResponse extends IHighchartsBaseResponse {
  series: ISeries[];
  xAxis: IXAxis;
  yAxis: IYAxis;
}

export interface IHighchartsPieResponse extends IHighchartsBaseResponse {
  series: IPieSeries[];
  category?: string;
}

export interface IEmbedPreviewV3Payload {
  savedViewId: string;
  version: string;
  reportVersion: string;
  shouldSync: boolean;
  disableClick: boolean;
  reportId: string;
  title?: string;
  description?: string;
  type?: string;
  id?: string;
  controller?: AbortController;
}

export interface IEmbedPreviewV2Payload {
  saved_view_id: string;
  version: string;
  report_version: string;
  should_sync: boolean;
  disable_click: boolean;
  report_id: string;
  title?: string;
  description?: string;
  type?: string;
  id?: string;
  group_data_by?: string;
  table_columns?: string[];
  display_account_codes?: boolean;
  controller?: AbortController;
}

export interface IEmbedV2Base {
  saved_view_id: string;
  version: string;
  report_version: string;
  should_sync: boolean;
  disable_click?: boolean;
  report_id: string;
  title?: string;
  description?: string;
  type?: string;
  group_data_by?: string;
  table_columns?: string[];
  display_account_codes?: boolean;
}

export interface IEmbedV2 extends IEmbedV2Base {
  id: string;
  data: unknown;
}

export interface IEmbedV2BaseResponse {
  savedViewId: string;
  version: string;
  report_version: string;
  shouldSync: boolean;
  disable_click?: boolean;
  reportId: string;
  title?: string;
  description?: string;
  type?: string;
  group_data_by?: string;
  table_columns?: string[];
  display_account_codes?: boolean;
}

export interface IEmbedV2Response extends IEmbedV2BaseResponse {
  id: string;
  data: unknown;
}

export interface IEmbedV4Base {
  title: string;
  description: string;
  privateUrl: string;
  publicUrl: string;
  reportId: string;
  savedViewId: string;
  shouldSync: boolean;
  type: EmbedTypeV4;
  reportVersion: string;
}
export interface IEmbedV3Base {
  title: string;
  description: string;
  privateUrl: string;
  publicUrl: string;
  reportId: string;
  savedViewId: string;
  shouldSync: boolean;
  type: EmbedTypeV3;
  reportVersion: string;
}

export interface IEmbedV3 extends IEmbedV3Base {
  createdAt: string;
  id: string;
  isDeleted: boolean;
  isStale: boolean;
  isUpdating: boolean;
  privateUrl: string;
  publicUrl: string;
  updatedAt: string;
  version: 'v3';
  data?: IEmbedDataV3;
  metadata?: IReportConfiguration['metadata'];
}

export interface IEmbedV4 extends IEmbedV4Base {
  createdAt: string;
  id: string;
  isDeleted: boolean;
  isStale: boolean;
  isUpdating: boolean;
  privateUrl: string;
  publicUrl: string;
  updatedAt: string;
  version: 'v4';
  data?: IEmbedDataV4;
  metadata?: IReportConfiguration['metadata'];
}

export type IEmbedDataV3 = (IHighchartsResponse | IHighchartsPieResponse) & {
  formatting: IAccountSummaryFormatting;
  lastUpdatedAt: string;
  options?: IMdsColRowFieldsOptions;
  chartProps?: any;
  labelsEnabled?: boolean;
  axisTitlesEnabled?: boolean;
};

export type IEmbedV3DbBase = IEmbedV3Base & { version: 'v3'; data: IEmbedDataV3 };

export type IEmbedDataV4 = {
  formatting: IAccountSummaryFormatting;
  lastUpdatedAt: string;
  chartProps: any | null;
};

export type IEmbedV4DbBase = IEmbedV4Base & { version: 'v4'; data: IEmbedDataV4 };

export type IEmbedSdkClientData = {
  isClientRendered: true;
  componentType: string;
  componentProps: any; // eventually "ITableTileProps | IPieTileProps | IBreakdownBarTileProps" ... etc
};

export type AmountTypes = 'incremental' | 'cumulative' | '';

export type ReportTypes = 'ytd' | 'annual' | 'balance' | 'grid';

export type LedgerTypes =
  | 'expenses'
  | 'assets'
  | 'revenues'
  | 'liabilities'
  | 'revenues_less_expenses'
  | 'assets_less_liabilities_and_equities'
  | 'fteCount'
  | 'scalar'
  | 'USD';
export interface TileData {
  title: string;
  description: string;
  privateUrl: string;
  publicUrl: string;
  amount: AmountTypes;
  lastUpdated: string;
  dataUpdatedAt?: string;
  // tslint:disable-next-line:no-reserved-keywords
  type: LedgerTypes;
  reportType: ReportTypes;
  isProrated: boolean;
}

export interface InteractionData {
  timePeriod: string;
}

export type DataType =
  | 'tile_gauge'
  | 'tile_bar'
  | 'tile_line'
  | 'tile_split_bar'
  | 'tile_line_grid'
  | 'tile_amount_budget_bar'
  | 'tile_percent_bullet_bar'
  | 'tile_bar_target_grid'
  | 'tile_bar_grid';

export interface InteractionMap<T extends InteractionData> {
  [key: string]: T;
}

export interface RenderData<T extends TileData, R extends InteractionData> {
  isClientRendered: false;
  // tslint:disable-next-line:no-reserved-keywords
  type: DataType;
  tile: T;
  interactions: InteractionMap<R>;
}

export type IEmbedSdkServerData = RenderData<TileData, InteractionData>;

export type IEmbedSdkData = IEmbedSdkServerData | IEmbedSdkClientData;

export interface IEmbedData<D extends IEmbedSdkData> {
  data: D;
  css?: string;
  html: string;
}
